import React from "react";
import cn from "classnames";
import styles from "../styles.modules.scss";
import { FormattedMessage } from "react-intl";
import { Button } from "../../../button";
import { Image } from "../../../image";
import { useElementOnScreen } from "../../../../hooks/use-element-on-screen";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { handleCTAClick } from "..";
import { parseCTAs } from "../parse-ctas";
import { usePollContext } from "../../../../stores/poll";
import { bridge } from "../../../../utils/bridge";

export type ProgressSlideType = {
    type: "progressTrackerEntry";
    id: string;
    title: string;
    tracker: ProgressTracker;
    milestones: ProgressMilestone[];
    ctas: InboxCTA[];
    targeting: Targeting;
    lang: Locale;
};

export const MeleeMendingProgressSlide = ({
    slide,
}: {
    slide: ProgressSlideType;
}) => {
    const { title, tracker, milestones, targeting } = slide;

    const [ref, isVisible] = useElementOnScreen<HTMLDivElement>({
        threshold: 0.001,
    });
    const bottomElementRef = React.useRef<HTMLDivElement>(null);
    const firstLockedRef = React.useRef<HTMLDivElement>(null);
    const trackerProgressRef = React.useRef<HTMLDivElement>(null);
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const {
        actions: { openModal },
    } = usePollContext();

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );

    React.useEffect(() => {
        if (bottomElementRef.current && isVisible) {
            const top = bottomElementRef.current.offsetTop;
            ref.current?.parentElement?.scrollTo({
                top,
            });
        }
    }, [bottomElementRef.current, isVisible]);

    React.useEffect(() => {
        setTimeout(() => {
            if (firstLockedRef.current && isVisible) {
                // Scroll the top of the filled bar to the center of the view
                const trackerProgressOffsetTop =
                    trackerProgressRef.current?.offsetTop ?? 0;
                const trackersOffsetTop =
                    firstLockedRef.current.parentElement?.parentElement
                        ?.offsetTop ?? 0;
                const trackersMarginTop = firstLockedRef.current.parentElement
                    ?.parentElement
                    ? parseFloat(
                          getComputedStyle(
                              firstLockedRef.current.parentElement
                                  ?.parentElement,
                          ).marginTop,
                      )
                    : 0;
                const contentPaddingTop = firstLockedRef.current.parentElement
                    ?.parentElement?.parentElement
                    ? parseFloat(
                          getComputedStyle(
                              firstLockedRef.current.parentElement
                                  ?.parentElement?.parentElement,
                          ).paddingTop,
                      )
                    : 0;

                const top =
                    trackerProgressOffsetTop +
                    trackersOffsetTop +
                    trackersMarginTop +
                    contentPaddingTop -
                    (ref.current?.parentElement?.clientHeight ?? 0) / 1.5;

                ref.current?.parentElement?.scrollTo({
                    top,
                    behavior: "smooth",
                });
            }
        }, 500);
    }, [bottomElementRef.current, isVisible]);

    const ctas = parseCTAs(slide.ctas, handleClick);

    const fillPercentage = tracker.progress;
    const barColor = tracker.color;

    const style = {
        height: `${Math.max(fillPercentage, 1.25)}%`,
    };

    const hasMileStoneLabels = milestones.some(
        (milestone) => !!milestone.label,
    );

    const barStyle = {
        "--bar-color": barColor,
        height: `calc(43.5rem + (${milestones.length}*20vh))`,
    } as React.CSSProperties;

    const firstLockedProgress =
        milestones.find((milestone) => !milestone.unlocked)?.progress ??
        tracker.progress;

    return (
        <>
            <div
                className={`slide progress-indicator ${styles.slide} ${
                    isVisible ? "visible" : ""
                } ${hasMileStoneLabels ? "with-milestone-labels" : ""} ${
                    targeting?.campaignId ?? ""
                } without-title`}
                ref={ref}
                style={barStyle}
            >
                <div className={`content ${styles.content}`}>
                    <div className={`logoWrapper ${styles.logoWrapper}`}>
                        <div className={`logo ${styles.logo}`}></div>
                        <div className={`shine ${styles.shine}`}></div>
                    </div>

                    <div className={`trackers ${styles.trackers}`}>
                        {isVisible && (
                            <div className={`tracker-wrapper`}>
                                <div
                                    className={`tracker ${styles.tracker} ${tracker.progress === 100 ? "full" : ""}`}
                                >
                                    <div
                                        style={style}
                                        className={`tracker-progress ${
                                            styles["tracker-progress"]
                                        } ${
                                            fillPercentage > 99
                                                ? "almost-complete"
                                                : ""
                                        } `}
                                        ref={trackerProgressRef}
                                    ></div>
                                </div>

                                {milestones.map((milestone, index) => {
                                    const milestonePosition = Math.max(
                                        milestone.progress,
                                        5, // 5% is the minimum we support without the UI looking totally wack
                                    );

                                    const style = {
                                        bottom: `${milestonePosition}%`,
                                    };

                                    return (
                                        <span
                                            key={index}
                                            style={style}
                                            className="milestone-marker"
                                        />
                                    );
                                })}
                            </div>
                        )}

                        <div
                            className={`milestone-wrapper ${styles["milestone-wrapper"]} milestones-${milestones.length}`}
                        >
                            {milestones.map((milestone, index) => {
                                if (!milestone.image) {
                                    return null;
                                }

                                const milestonePosition = Math.max(
                                    milestone.progress,
                                    5, // 5% is the minimum we support without the UI looking totally wack
                                );

                                const hasReachedMilestone = milestone.unlocked;
                                const milestoneIsNotShopOffer = [
                                    0, 2, 4, 6, 8, 11, 14,
                                ].includes(index);

                                const style = {
                                    bottom: `${milestonePosition}%`,
                                };

                                return (
                                    <div
                                        style={style}
                                        key={index}
                                        onClick={async () => {
                                            if (milestoneIsNotShopOffer) return;
                                            if (hasReachedMilestone) {
                                                // Direct everyone to the shop, as the Chronos offers are not
                                                // published automatically
                                                (await bridge()).openShop();
                                            }
                                        }}
                                        ref={
                                            milestone.progress ===
                                            firstLockedProgress
                                                ? firstLockedRef
                                                : null
                                        }
                                        className={cn(
                                            "milestone-img",
                                            styles["milestone-img"],
                                            {
                                                "milestone-achieved":
                                                    hasReachedMilestone,
                                            },

                                            {
                                                "milestone-unachieved":
                                                    !hasReachedMilestone,
                                            },
                                        )}
                                    >
                                        <Image sources={milestone.image} />

                                        {!hasReachedMilestone && (
                                            <div className="milestone-locked" />
                                        )}
                                        {hasReachedMilestone && (
                                            <div className="milestone-unlocked" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div
                            className={`milestone-label-wrapper ${styles["milestone-label-wrapper"]}`}
                        >
                            {milestones.map((milestone, index) => {
                                if (!milestone.label) {
                                    return <div key={index} />;
                                }

                                const milestonePosition = Math.max(
                                    milestone.progress,
                                    5, // 5% is the minimum we support without the UI looking totally wack
                                );

                                const hasReachedMilestone = milestone.unlocked;

                                const style = {
                                    bottom: `${milestonePosition}%`,
                                };

                                return (
                                    <div
                                        key={index}
                                        style={style}
                                        className={`milestone-label ${
                                            styles["milestone-label"]
                                        } ${
                                            hasReachedMilestone
                                                ? styles["milestone-reached"]
                                                : styles["milestone-unreached"]
                                        } ${
                                            hasReachedMilestone
                                                ? "milestone-reached"
                                                : "milestone-unreached"
                                        }`}
                                        title={milestone.label}
                                    >
                                        {milestone.label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div ref={bottomElementRef}></div>
                </div>
            </div>
            <div className={`wrapper ${styles.wrapper}`}>
                {ctas.length ? (
                    <div
                        className={`ctas ${
                            ctas.length === 1 ? "single" : "multi"
                        } ${styles.ctas}`}
                    >
                        {ctas.map(({ label, variant, onClick }, index) => {
                            return (
                                <Button
                                    key={index}
                                    onClick={onClick}
                                    variant={variant}
                                >
                                    <span className="buttonTextWrapper">
                                        {label || (
                                            <FormattedMessage id="globalText.langGo" />
                                        )}
                                    </span>
                                </Button>
                            );
                        })}
                    </div>
                ) : null}
            </div>
        </>
    );
};
